/**
 * VerdictCard — one verified citation (F6-6 下钻面板).
 *
 * Collapsed: composite badge + citation title + node-deleted marker + the three
 * dimension chips. Expanded (drill-down, PRD §F6-6): 引用原文 → 核到的法条（含
 * 原文）→ 核验依据. Self-contained from the GET payload + nodeRef; never needs to
 * anchor into the diff DOM.
 */
import { useState } from 'react';

import type { VerificationResult, VerdictConfidence } from '../../lib/veridoc-api';

import { VALIDITY_LABELS } from './constants';
import { DimensionPills } from './DimensionPills';
import { VerdictBadge } from './VerdictBadge';
import { TONE_CLASSES, VALIDITY_TONE } from './verdictVisual';

const CONFIDENCE_LABELS: Record<VerdictConfidence, string> = {
  'rule-based': '事实判断',
  high: 'AI 高把握',
  medium: 'AI 存疑',
  low: 'AI 未评',
};

const CONFIDENCE_TONE: Record<VerdictConfidence, string> = {
  'rule-based': 'bg-ink-100 text-ink-600',
  high: 'bg-semantic-successBg text-semantic-success',
  medium: 'bg-semantic-warningBg text-semantic-warning',
  low: 'bg-ink-100 text-ink-500',
};

function citationTitle(
  lawName: string | null,
  articleNo: string | null,
  rawText: string,
): string {
  const name = (lawName ?? '').trim();
  const article = (articleNo ?? '').trim();
  if (name && article) {
    const wrapped = name.startsWith('《') ? name : `《${name}》`;
    return `${wrapped}第${article}条`;
  }
  // Vague / unparsed reference — show the raw quote rather than fabricate 《…》.
  return rawText.trim() || name || '未识别引用';
}

export function VerdictCard({
  result,
}: {
  result: VerificationResult;
}): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const { citation, matchedStatute, verdict } = result.payload;

  return (
    <li className="rounded-lg border border-ink-200 bg-ink-0 shadow-sm">
      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <VerdictBadge overall={verdict.overall} />
              {verdict.confidence && (
                <span
                  title="核验结论的可信度等级。事实判断来自法条库的硬数据；AI 等级反映 V3 一致性判断的把握度。"
                  className={`inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-medium ${
                    CONFIDENCE_TONE[verdict.confidence]
                  }`}
                >
                  {CONFIDENCE_LABELS[verdict.confidence]}
                </span>
              )}
              <span className="truncate text-sm font-semibold text-ink-900">
                {citationTitle(citation.lawName, citation.articleNo, citation.rawText)}
              </span>
              {result.nodeDeleted && (
                <span className="inline-flex items-center rounded-sm bg-ink-100 px-1.5 py-0.5 text-xs text-ink-500">
                  节点已删除
                </span>
              )}
            </div>
            <DimensionPills verdict={verdict} />
          </div>

          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="inline-flex h-btn-sm shrink-0 items-center gap-1 rounded-sm border border-ink-200 bg-ink-50 px-2 text-xs text-ink-600 hover:bg-ink-100 hover:text-ink-900"
          >
            <span
              aria-hidden="true"
              className={`transition-transform duration-fast ${expanded ? 'rotate-90' : ''}`}
            >
              ›
            </span>
            {expanded ? '收起' : '下钻详情'}
          </button>
        </div>

        {expanded && (
          <div className="flex flex-col gap-3 border-t border-ink-100 pt-3">
            <Field label="引用原文">
              <p className="font-serif text-sm leading-relaxed text-ink-800">
                {citation.rawText || '（无引用片段）'}
              </p>
            </Field>

            <Field label="核到的法条">
              {matchedStatute ? (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-ink-900">
                      {citationTitle(
                        matchedStatute.lawName,
                        matchedStatute.articleNo,
                        '',
                      )}
                    </span>
                    <span
                      className={`inline-flex items-center rounded-sm px-1.5 py-0.5 text-xs ${
                        TONE_CLASSES[VALIDITY_TONE[verdict.validity]].pill
                      }`}
                    >
                      {VALIDITY_LABELS[verdict.validity]}
                    </span>
                  </div>
                  <StatuteMeta
                    effectiveDate={matchedStatute.effectiveDate}
                    version={matchedStatute.version}
                  />
                  {matchedStatute.fullText && (
                    <blockquote className="rounded-md border-l-2 border-ink-300 bg-ink-50 px-3 py-2 font-serif text-sm leading-relaxed text-ink-700">
                      {matchedStatute.fullText}
                    </blockquote>
                  )}
                </div>
              ) : (
                <p className="text-sm text-ink-500">未匹配到来源法条。</p>
              )}
            </Field>

            <Field label="核验依据">
              <p className="text-sm leading-relaxed text-ink-700">
                {verdict.rationale || '（无）'}
              </p>
            </Field>
          </div>
        )}
      </div>
    </li>
  );
}

function StatuteMeta({
  effectiveDate,
  version,
}: {
  effectiveDate: string | null;
  version: string | null;
}): React.JSX.Element | null {
  const parts: string[] = [];
  if (effectiveDate) parts.push(`施行 ${effectiveDate}`);
  if (version) parts.push(`版本 ${version}`);
  if (parts.length === 0) return null;
  return (
    <p className="text-xs text-ink-500">
      {parts.map((p, i) => (
        <span key={i}>
          {i > 0 && <span className="mx-1.5 text-ink-300">·</span>}
          {p}
        </span>
      ))}
    </p>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wide text-ink-400">
        {label}
      </span>
      {children}
    </div>
  );
}

export default VerdictCard;
